/**
 * Sentra brand mark — cut-paper geometry from the Sentra design system
 * (Branding/ds-bundle). Single-colour rendering only: white on dark
 * surfaces, ink on light ones. Used sparingly — identity, not decoration.
 */

const FACETS = [
  'M50.00 12.48 L 62.40 33.73 L 50.00 44.35 L 37.60 33.73 Z',
  'M36.00 35.65 L 48.40 46.27 L 37.85 64.34 L 23.72 46.17 Z',
  'M64.00 35.65 L 76.28 46.17 L 62.15 64.34 L 51.60 46.27 Z',
  'M39.45 66.57 L 50.00 48.48 L 60.55 66.57 L 50.00 84.66 Z',
  'M24.73 51.54 L 36.42 66.57 L 30.57 74.08 Z',
  'M75.27 51.54 L 69.43 74.08 L 63.58 66.57 Z',
];

const INK = '#16151a';

interface SentraMarkProps {
  size?: number;
  /** CSS colour for the mark; defaults to currentColor */
  color?: string;
  className?: string;
}

export function SentraMark({ size = 40, color = 'currentColor', className }: SentraMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="7.4 5.5 85.2 85.2"
      aria-hidden
      className={className}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {FACETS.map((d, i) => (
        <path key={i} d={d} fill={color} />
      ))}
    </svg>
  );
}

interface SentraWordmarkProps {
  /** 'dark' = pure white on the indigo rail; 'light' = ink on light surfaces */
  tone?: 'light' | 'dark';
  size?: number;
  className?: string;
}

// Ink bounds of FACETS: the square viewBox above pads the mark ~16% per side,
// which inflates the gap to the wordmark and floats the mark off the text.
const INK_BOX = { x: 23.72, y: 12.48, w: 52.56, h: 72.18 };

/** Mark + "Sentra" in the brand serif (DM Serif Display). */
export function SentraWordmark({ tone = 'light', size = 30, className }: SentraWordmarkProps) {
  const color = tone === 'dark' ? '#ffffff' : INK;
  const fontSize = size * 0.87;
  // Lockup: the mark stands on the text baseline and overshoots cap height
  // slightly (pointed shapes need overshoot to look as tall as flat caps).
  const markH = fontSize * MARK_TO_FONT;
  return (
    <span
      className={`inline-flex items-baseline ${className || ''}`}
      style={{ gap: fontSize * 0.28, color }}
    >
      <svg
        width={(markH * INK_BOX.w) / INK_BOX.h}
        height={markH}
        viewBox={`${INK_BOX.x} ${INK_BOX.y} ${INK_BOX.w} ${INK_BOX.h}`}
        aria-hidden
        style={{ flexShrink: 0, alignSelf: 'baseline' }}
      >
        {FACETS.map((d, i) => (
          <path key={i} d={d} fill={color} />
        ))}
      </svg>
      <span className="leading-none" style={{ fontFamily: "'DM Serif Display', serif", fontSize }}>
        Sentra
      </span>
    </span>
  );
}

// ponytail: tuned by eye against DM Serif Display's cap height; retune if the serif changes
const MARK_TO_FONT = 0.78;
